// BidIQ — Search Bar + Results Panel Component
import { useState, useRef, useEffect, useMemo } from "react";
import { marked } from "marked";
import CL from "./theme";
import useAssistant from "./useAssistant";
import { exportPDF, exportWord, exportExcel, copyToClipboard } from "./exportDocument";

// Configure marked for clean output
marked.setOptions({ breaks: true, gfm: true });

// ── Inline Styles ──

const S = {
  wrapper: {
    position: "relative",
    flex: 1,
    maxWidth: 560,
    margin: "0 20px",
  },
  form: {
    display: "flex",
    alignItems: "center",
    background: CL.card,
    border: `1px solid ${CL.border}`,
    borderRadius: 10,
    padding: "0 4px",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  formFocused: {
    borderColor: CL.gold,
    boxShadow: `0 0 0 2px ${CL.gold}33`,
  },
  searchIcon: {
    padding: "0 10px",
    color: CL.muted,
    fontSize: 14,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  input: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: CL.text,
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    padding: "9px 0",
    width: "100%",
  },
  micBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px 8px",
    borderRadius: 6,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.15s",
  },
  sendBtn: {
    background: CL.gold,
    border: "none",
    borderRadius: 6,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 12px",
    cursor: "pointer",
    flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif",
    marginLeft: 4,
  },
  panel: {
    position: "fixed",
    top: 60,
    left: "50%",
    transform: "translateX(-50%)",
    width: 560,
    background: CL.card,
    border: `1px solid ${CL.border}`,
    borderRadius: 12,
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    zIndex: 1000,
    maxHeight: "calc(100vh - 80px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderBottom: `1px solid ${CL.border}`,
    flexShrink: 0,
  },
  panelTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: CL.gold,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  panelActions: {
    display: "flex",
    gap: 6,
  },
  smallBtn: {
    background: "none",
    border: `1px solid ${CL.border}`,
    borderRadius: 5,
    color: CL.dim,
    fontSize: 10,
    padding: "3px 8px",
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "border-color 0.15s, color 0.15s",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 16px",
  },
  userMsg: {
    background: `${CL.gold}18`,
    border: `1px solid ${CL.gold}30`,
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 10,
    fontSize: 13,
    color: CL.text,
    fontFamily: "'DM Sans', sans-serif",
    textAlign: "right",
  },
  assistantMsg: {
    background: CL.bg,
    border: `1px solid ${CL.border}`,
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 10,
    fontSize: 13,
    color: CL.text,
    lineHeight: 1.65,
    fontFamily: "'DM Sans', sans-serif",
  },
  exportBar: {
    display: "flex",
    gap: 6,
    marginTop: 10,
    paddingTop: 8,
    borderTop: `1px solid ${CL.border}`,
    flexWrap: "wrap",
  },
  exportBtn: {
    background: CL.bg,
    border: `1px solid ${CL.border}`,
    borderRadius: 5,
    color: CL.dim,
    fontSize: 10,
    fontWeight: 600,
    padding: "4px 10px",
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "border-color 0.15s, color 0.15s, background 0.15s",
  },
  errorBox: {
    background: `${CL.lost}15`,
    border: `1px solid ${CL.lost}40`,
    borderRadius: 8,
    padding: "8px 12px",
    margin: "0 16px 10px",
    fontSize: 11,
    color: CL.lost,
  },
  loading: {
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: CL.gold,
    animation: "bidiq-pulse 1s infinite",
    marginLeft: 4,
  },
};

// ── Markdown Renderer ──

function MarkdownContent({ content }) {
  const html = useMemo(() => {
    if (!content) return "";
    return marked.parse(content);
  }, [content]);

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ wordBreak: "break-word" }}
      className="bidiq-md"
    />
  );
}

// ── Main Component ──

export default function AssistantBar({ bids, priceIntel }) {
  const {
    messages, isLoading, isListening, error,
    inputValue, setInputValue, sendMessage, stopStreaming,
    toggleMic, hasSpeech, clearHistory,
  } = useAssistant({ bids, priceIntel });

  const [focused, setFocused] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Show panel when there are messages or loading
  const showPanel = panelOpen || messages.length > 0 || isLoading;

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Close panel on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === "Escape") {
        setPanelOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setPanelOpen(true);
      sendMessage(inputValue);
    }
  };

  const handleCopy = async (content, msgIdx) => {
    await copyToClipboard(content);
    setCopyFeedback(msgIdx);
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  return (
    <div style={S.wrapper} ref={panelRef}>
      {/* Inject keyframe animation */}
      <style>{`
        @keyframes bidiq-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes bidiq-mic { 0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); } 50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); } }
        .bidiq-md h1 { font-size: 16px; font-weight: 700; color: ${CL.gold}; margin: 12px 0 6px; }
        .bidiq-md h2 { font-size: 14px; font-weight: 700; color: ${CL.gold}; margin: 10px 0 5px; }
        .bidiq-md h3 { font-size: 13px; font-weight: 700; color: ${CL.text}; margin: 8px 0 4px; }
        .bidiq-md p { margin: 4px 0; }
        .bidiq-md ul, .bidiq-md ol { margin: 4px 0 4px 18px; padding: 0; }
        .bidiq-md li { margin: 2px 0; }
        .bidiq-md strong { color: ${CL.text}; font-weight: 700; }
        .bidiq-md code { background: ${CL.bg}; padding: 1px 5px; border-radius: 3px; font-family: 'JetBrains Mono', monospace; font-size: 11px; }
        .bidiq-md pre { background: ${CL.bg}; padding: 10px; border-radius: 6px; overflow-x: auto; margin: 6px 0; }
        .bidiq-md pre code { padding: 0; }
        .bidiq-md table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
        .bidiq-md th { background: ${CL.navy}; color: #fff; padding: 6px 8px; text-align: left; font-weight: 700; }
        .bidiq-md td { padding: 5px 8px; border-bottom: 1px solid ${CL.border}; }
        .bidiq-md tr:nth-child(even) { background: ${CL.bg}; }
        .bidiq-md blockquote { border-left: 3px solid ${CL.gold}; margin: 6px 0; padding: 4px 12px; color: ${CL.dim}; }
      `}</style>

      {/* Search Bar */}
      <form
        onSubmit={handleSubmit}
        style={{ ...S.form, ...(focused ? S.formFocused : {}) }}
      >
        <span style={S.searchIcon}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onFocus={() => { setFocused(true); if (messages.length > 0) setPanelOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder="Ask BidIQ anything..."
          style={S.input}
          autoComplete="off"
        />
        {isLoading && (
          <button type="button" onClick={stopStreaming} style={{ ...S.micBtn, color: CL.lost }} title="Stop">
            {"■"}
          </button>
        )}
        {hasSpeech && (
          <button
            type="button"
            onClick={toggleMic}
            style={{
              ...S.micBtn,
              color: isListening ? CL.lost : CL.muted,
              background: isListening ? `${CL.lost}20` : "transparent",
              animation: isListening ? "bidiq-mic 1.5s infinite" : "none",
            }}
            title={isListening ? "Stop listening" : "Voice input"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
          </button>
        )}
        <button
          type="submit"
          style={{ ...S.sendBtn, opacity: inputValue.trim() ? 1 : 0.4 }}
          disabled={!inputValue.trim() || isLoading}
        >
          Ask
        </button>
      </form>

      {/* Results Panel (dropdown) */}
      {showPanel && (
        <div style={S.panel}>
          <div style={S.panelHeader}>
            <span style={S.panelTitle}>BidIQ</span>
            <div style={S.panelActions}>
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  style={S.smallBtn}
                  onMouseEnter={e => { e.target.style.color = CL.text; e.target.style.borderColor = CL.muted; }}
                  onMouseLeave={e => { e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setPanelOpen(false)}
                style={S.smallBtn}
                onMouseEnter={e => { e.target.style.color = CL.text; e.target.style.borderColor = CL.muted; }}
                onMouseLeave={e => { e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
              >
                ✕
              </button>
            </div>
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <div style={S.messagesArea}>
            {messages.map((msg, i) => (
              <div key={i}>
                {msg.role === "user" ? (
                  <div style={S.userMsg}>{msg.content}</div>
                ) : (
                  <div style={S.assistantMsg}>
                    <MarkdownContent content={msg.content} />
                    {msg.content && !isLoading && (
                      <div style={S.exportBar}>
                        <button
                          onClick={() => exportPDF(msg.content)}
                          style={S.exportBtn}
                          onMouseEnter={e => { e.target.style.background = CL.card; e.target.style.color = CL.text; e.target.style.borderColor = CL.gold; }}
                          onMouseLeave={e => { e.target.style.background = CL.bg; e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
                        >
                          {"📄 PDF"}
                        </button>
                        <button
                          onClick={() => exportWord(msg.content)}
                          style={S.exportBtn}
                          onMouseEnter={e => { e.target.style.background = CL.card; e.target.style.color = CL.text; e.target.style.borderColor = CL.gold; }}
                          onMouseLeave={e => { e.target.style.background = CL.bg; e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
                        >
                          {"📝 Word"}
                        </button>
                        <button
                          onClick={() => exportExcel(msg.content)}
                          style={S.exportBtn}
                          onMouseEnter={e => { e.target.style.background = CL.card; e.target.style.color = CL.text; e.target.style.borderColor = CL.gold; }}
                          onMouseLeave={e => { e.target.style.background = CL.bg; e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
                        >
                          {"📊 Excel"}
                        </button>
                        <button
                          onClick={() => handleCopy(msg.content, i)}
                          style={S.exportBtn}
                          onMouseEnter={e => { e.target.style.background = CL.card; e.target.style.color = CL.text; e.target.style.borderColor = CL.gold; }}
                          onMouseLeave={e => { e.target.style.background = CL.bg; e.target.style.color = CL.dim; e.target.style.borderColor = CL.border; }}
                        >
                          {copyFeedback === i ? "✓ Copied" : "📋 Copy"}
                        </button>
                      </div>
                    )}
                    {isLoading && i === messages.length - 1 && (
                      <span style={S.loading} />
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}
